import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, Trash2, History } from "lucide-react";
import { aiQuick } from "../api";

const BTN_ROWS = [
  ["C", "⌫", "%", "÷"],
  ["7", "8", "9", "×"],
  ["4", "5", "6", "−"],
  ["1", "2", "3", "+"],
  ["±", "0", ".", "="],
];

const MAP = { "÷": "/", "×": "*", "−": "-", "+": "+" };

export default function Calculator() {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("");
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hexa_calc_history") || "[]"); } catch { return []; }
  });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem("hexa_calc_history", JSON.stringify(history.slice(0, 20)));
  }, [history]);

  const evaluate = (s) => {
    try {
      const cleaned = s.replace(/[÷×−]/g, c => MAP[c]).replace(/%/g, "/100");
      if (!/^[0-9+\-*/().\s]+$/.test(cleaned)) return "";
      // eslint-disable-next-line no-new-func
      const v = Function(`"use strict"; return (${cleaned})`)();
      if (typeof v !== "number" || !isFinite(v)) return "";
      return String(parseFloat(v.toFixed(10)));
    } catch { return ""; }
  };

  const press = (k) => {
    if (k === "C") { setExpr(""); setResult(""); return; }
    if (k === "⌫") { setExpr(e => e.slice(0, -1)); return; }
    if (k === "±") {
      if (!expr) return;
      if (expr.startsWith("-")) setExpr(expr.slice(1));
      else setExpr("-" + expr);
      return;
    }
    if (k === "=") {
      const r = evaluate(expr);
      if (r !== "") {
        setHistory(h => [{ expr, result: r, ts: Date.now() }, ...h].slice(0, 20));
        setResult(r);
      }
      return;
    }
    setExpr(e => e + k);
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
      const k = e.key;
      if (/[0-9.+\-*/()%]/.test(k)) { setExpr(x => x + k); e.preventDefault(); }
      else if (k === "Enter" || k === "=") { press("="); e.preventDefault(); }
      else if (k === "Backspace") { press("⌫"); e.preventDefault(); }
      else if (k === "Escape") { press("C"); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [expr]);

  useEffect(() => { setResult(evaluate(expr)); }, [expr]);

  const askAI = async () => {
    if (!aiQ.trim()) return;
    setAiBusy(true);
    setAiOut("");
    try {
      const r = await aiQuick(
        `Solve this math/calculation problem and return only the numeric or symbolic answer with a one-line explanation. Question: ${aiQ}`,
        "You are a math assistant. Be concise. If user asks for an arithmetic answer, return the number first.",
      );
      setAiOut(r.text);
    } catch { setAiOut("AI unavailable."); }
    setAiBusy(false);
  };

  return (
    <div className="h-full flex" data-testid="app-calculator">
      <div className="flex-1 flex flex-col p-4">
        <div className="bg-black/40 border border-white/10 rounded-xl p-4 mb-3">
          <div className="text-right text-xs text-white/40 mono break-all min-h-[18px]" data-testid="calc-expr">{expr || " "}</div>
          <div className="text-right text-3xl mono font-light mt-1" data-testid="calc-result">{result || "0"}</div>
        </div>
        <div className="grid grid-cols-4 gap-2 flex-1">
          {BTN_ROWS.flat().map((k) => {
            const isOp = "÷×−+=".includes(k);
            const isFn = "C⌫%±".includes(k);
            return (
              <button
                key={k}
                data-testid={`calc-btn-${k}`}
                onClick={() => press(k)}
                className={`rounded-xl text-lg font-light tracking-tight transition active:scale-95 ${
                  k === "="
                    ? "bg-[#ff7a2e] text-white col-span-1"
                    : isOp
                    ? "bg-white/10 hover:bg-white/15 text-[#ff7a2e]"
                    : isFn
                    ? "bg-white/5 hover:bg-white/10 text-white/70"
                    : "bg-white/[0.07] hover:bg-white/12"
                }`}
              >
                {k}
              </button>
            );
          })}
        </div>
        <button onClick={() => setAiOpen(!aiOpen)} data-testid="calc-ai-toggle" className="mt-3 btn-ghost text-xs flex items-center justify-center gap-1">
          <Sparkles size={12} color="#ff7a2e" /> {aiOpen ? "Hide" : "Ask"} HEXA Math
        </button>
        {aiOpen && (
          <div className="mt-2 p-2 border border-white/10 rounded-lg bg-black/30">
            <div className="flex gap-1">
              <input
                data-testid="calc-ai-input"
                value={aiQ}
                onChange={(e) => setAiQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && askAI()}
                placeholder="e.g. 15% tip on $42.50"
                className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs"
              />
              <button data-testid="calc-ai-ask" onClick={askAI} className="btn-accent text-xs flex items-center gap-1">
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              </button>
            </div>
            {aiOut && <div className="text-xs text-white/85 mt-2 whitespace-pre-wrap">{aiOut}</div>}
          </div>
        )}
      </div>

      <div className="w-56 border-l border-white/10 bg-black/30 flex flex-col">
        <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs uppercase tracking-widest text-white/40">
          <span className="flex items-center gap-1.5"><History size={11} /> History</span>
          {history.length > 0 && (
            <button onClick={() => setHistory([])} data-testid="calc-clear-history"><Trash2 size={11} className="text-red-400" /></button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {history.length === 0 && <div className="text-xs text-white/40 text-center mt-6">No calculations yet</div>}
          {history.map((h, i) => (
            <button
              key={i}
              onClick={() => { setExpr(h.expr); setResult(h.result); }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-white/5"
            >
              <div className="text-[10px] text-white/40 mono truncate">{h.expr}</div>
              <div className="text-sm mono text-[#ff7a2e]">= {h.result}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
